import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TaskService, RequestService, UserService, SubletService, WebSocketService, TaskUpdatePayload } from '@org/shared-services';
import { User, Request, Task } from '@org/interfaces';
import { FormsModule } from '@angular/forms';
import { AuthService } from '@org/shared-services';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { SubletSelectionComponent } from '../../../subletSelection/subletSelection.component';
import { inspectFormData } from '@org/utilities';
import { catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';

export interface RecentRequest {
  type: string;
  date: Date;
  status: 'Completed' | 'In Progress' | 'Pending';
}

interface SubletPhoto {
  url: string;
  file: File;
  timestamp: number;
  rotation?: number;
  isProcessing?: boolean;
}


interface Sublet {
  name: string;
  photos: SubletPhoto[];
}


@Component({
    selector: 'app-create-task',
    imports: [CommonModule, FormsModule, SubletSelectionComponent],
    templateUrl: './create-task.component.html',
    styleUrl: './create-task.component.css',
    animations: [
        trigger('slideInOut', [
            state('in', style({
                transform: 'translateX(0)'
            })),
            transition(':enter', [
                style({ transform: 'translateX(100%)' }),
                animate('200ms ease-in')
            ]),
            transition(':leave', [
                animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
            ])
        ])
    ]
})
export class CreateTaskComponent implements OnInit {
  requests: Request[] = [];
  tenantUsers: User[] = [];
  selectedSubletOptions: any[] = [];
  user: any;
  dropdownOpen = false;
  slideOutVisible = false;
  mobileMenuVisible = false;
  selectedRequests: Request[] = [];
  selectedRequestName!: string;
  buttons: string[] = ['Blueprint', 'Supplement', 'Phase Change', 'Vehicle Complete', 'Supplies', 'Pull Parts', 'Return Parts', 'In Process Photos'];

  task = {
    user: '',
    employee: '',
    request: '',
    requestName: '',
    comments: '',
    isUrgent: false,
    tenant: '',
    sublets: []
  };

  options: { name: string, selected: boolean }[] = [
    { name: 'Striping', selected: false },
    { name: 'Glass', selected: false },
    { name: 'Alignment', selected: false },
    { name: 'Sensors', selected: false }
  ];
  selectedOptions: any[] = [];
  slideOutVisible2 = false;
  showSubletButton = false;

  // recentRequests: RecentRequest[] = [
  //   {
  //     type: 'Phase Change',
  //     date: new Date(),
  //     status: 'Completed'
  //   },
  //   {
  //     type: 'Supplies',
  //     date: new Date(),
  //     status: 'In Progress'
  //   },
  //   {
  //     type: 'Pull Parts',
  //     date: new Date(),
  //     status: 'Pending'
  //   }
  // ];

  currentCamera: 'environment' | 'user' = 'environment';
  flashEnabled = false;
  isCapturing = false;
  previewPhoto: SubletPhoto | null = null;
  isSubmitting = false;
  generalPhotos: SubletPhoto[] = [];

  constructor(
    private taskService: TaskService,
    private requestService: RequestService,
    private userService: UserService,
    private ws: WebSocketService,
    @Inject(AuthService) private authService: AuthService,
    private cdr: ChangeDetectorRef,
    public subletService: SubletService,
  ) {}

  ngOnInit(): void {
    this.reset();
    this.loadEmployee();
    this.loadRequests();
    this.loadSubletTasks();

    this.subletService.selectedOptions$.subscribe(options => {
      this.selectedSubletOptions = options;
    });
  }

  loadRequests() {
    this.requestService.getRequests().subscribe((data: Request[]) => {
      this.requests = data;
      this.buttons.forEach(v => {
        this.selectRequest(v);
       });
    });
  }

  loadEmployee(): void {
    this.user = this.authService.currentUserValue;
  }

  selectRequest(requestName: string): void {
    const request = this.requests.find(r => r.name === requestName);
    if (request && this.selectedRequests.length < 8 && !this.selectedRequests.find(r => r._id === request._id)) {
      this.selectedRequests.push(request);
      this.task.user = this.user
    }
  }

  selectedTask(requestName: string): void {
    const request = this.requests.find(r => r.name === requestName);
    this.task.requestName = <string>request?.name;
    this.task.request = <string>request?._id;
  }

  
  async onSubmit() {
    try {
      // New task data
      const newTask = {
        employee: this.user._id,
        request: this.task.request,
        comments: this.task.comments,
        tenant: this.user.tenant,
        isMobile: true,
        isUrgent: this.task.isUrgent,
        sublets: this.subletService.selectedOptionsSubject.value.map(option => ({
          name: option.name
        }))
      };
      this.isSubmitting = true;
      let createdTaskId: string | null = null;
      // Check if this is a Vehicle Complete Request (with photos)
      if ((this.task.requestName === 'Vehicle Complete' ||
        this.task.requestName === 'Supplement' ||
        this.task.requestName === 'Phase Change') &&
          this.subletService.selectedOptionsSubject.value.some(sublet => sublet.photos?.length > 0)) {
        const formData = new FormData();
        // Add task data with photos metadata
        const taskWithPhotos = {
          ...newTask,
          sublets: this.subletService.selectedOptionsSubject.value.map(option => ({
            name: option.name,
            photos: option.photos?.map((photo: { rotation: any; timestamp: any; }) => ({
              rotation: photo.rotation || 0,
              timestamp: photo.timestamp
            })) || []
          }))
        };
        formData.append('taskData', JSON.stringify(taskWithPhotos));
        // Add photos for each sublet
        this.subletService.selectedOptionsSubject.value.forEach((sublet, subletIndex) => {
          if (sublet.photos) {
            sublet.photos.forEach((photo: { file: Blob; rotation: any; }, photoIndex: any) => {
              formData.append(
                `file`, 
                photo.file,
                `${subletIndex}_${photoIndex}_${photo.rotation || 0}_${sublet.name}.jpg`
              );
            });
          }
        });
        const formDataInspect = inspectFormData(formData);
        // Submit with photos
        this.taskService.createRequestWithPhotos(formData).subscribe({
          next: async (createTask: {message: string, taskId: string, task: Task}) => {
            createdTaskId = createTask.taskId;
            this.notifyDashboardPeers(this.task.request);
            // Upload general photos if any
            if (this.generalPhotos.length > 0 && createdTaskId) {
              await this.taskService.uploadTaskMedia(createdTaskId, this.generalPhotos.map(p => p.file)).toPromise();
            }
            this.reset();
            this.toggleSlideOut();
            this.cdr.detectChanges();
            this.isSubmitting = false;
          },
          error: (error) => {
            console.error('Error creating task with photos:', error);
            this.isSubmitting = false;
          }
        });
      } else {
        // Submit without photos
        this.taskService.createRequest(newTask).subscribe({
          next: async (createTask: {message: string, taskId: string, task: Task}) => {
            createdTaskId = createTask.taskId;
            this.notifyDashboardPeers(this.task.request);
            // Upload general photos if any
            if (this.generalPhotos.length > 0 && createdTaskId) {
              await this.taskService.uploadTaskMedia(createdTaskId, this.generalPhotos.map(p => p.file)).toPromise();
            }
            this.reset();
            this.toggleSlideOut();
            this.cdr.detectChanges();
            this.isSubmitting = false;
          },
          error: (error) => {
            console.error('Error creating task:', error);
            this.isSubmitting = false;
          }
        });
      }
    } catch (error) {
      console.error('Error preparing task submission:', error);
      this.isSubmitting = false;
    }
  }

  reset(): void {
    this.task = {
      user: '',
      employee: '',
      request: '',
      requestName: '',
      comments: '',
      isUrgent: false,
      tenant: '',
      sublets: []
    };
    this.subletService.resetOptions(); // Reset selected sublets
    // Reset local options
    if (this.options) {
      this.options.forEach(option => {
        option.selected = false;
      });
    }
    this.syncOptionsWithService();
    // Reset any other form-related properties
    this.showSubletButton = false;
    this.isCapturing = false;
    this.flashEnabled = false;
    // Reset general photos
    this.generalPhotos.forEach(photo => URL.revokeObjectURL(photo.url));
    this.generalPhotos = [];
  }

  openSlideOut(taskName: string) {
    this.reset();
    this.selectedTask(taskName);
    if (taskName == "Vehicle Complete" || taskName == "Supplement" || taskName == "Phase Change") {
     // this.toggleSubletComponent();
     this.slideOutVisible = true;
     this.showSubletButton = true;
    } else {
      this.slideOutVisible = true;
    }
    this.cdr.detectChanges();
  }

  toggleSlideOut() {
    this.slideOutVisible = false;
    this.reset();
    this.showSubletButton = false;
    this.subletService.resetOptions();
    // this.slideOutVisible = !this.slideOutVisible;
    // if (this.slideOutVisible == false) {
    //   this.reset();
    // }
  }

  toggleUrgency(): void {
    this.task.isUrgent = !this.task.isUrgent;
  }

 

  // Remove sublet options from the array
  removeSubletOption(option: any) {
    this.subletService.removeOption(option);
  }

  /**
   * Sublet Controls
   */

   // Load sublet tasks from the database
   loadSubletTasks() {
    this.subletService.getSubletTasks().subscribe({
      next: (data) => {
        this.options = data.map(task => ({ name: task.name, selected: false }));
        this.syncOptionsWithService(); // Sync selected states after loading
      },
      error: (error) => {
        console.error('Error loading sublet tasks:', error);
      }
   });
  }

  // Synchronize the selected options with the service
  syncOptionsWithService() {
    const selectedOptions = this.subletService.selectedOptionsSubject.value;

    this.options.forEach(option => {
      option.selected = selectedOptions.some(selected => selected.name === option.name);
    });
  }

  selectOption(option: any) {
    option.selected = !option.selected;
    this.updateSelectedOptions();
  }

  updateSelectedOptions() {
    this.selectedOptions = this.options.filter(option => option.selected);
  }

  resetOptions() {
    this.options.forEach(option => {
      option.selected = false;
    });
    this.updateSelectedOptions();
    this.subletService.resetOptions(); // Reset all options
  }

  // selectNone() {
  //   this.subletService.resetOptions(); // Reset all options
  //   this.closeSlideOut(); // Close the slide-out
  //   this.syncOptionsWithService(); // Ensure the options are reset visually
  //   // Load Vehicle Complete
  //   this.selectedTask("Vehicle Complete");
  //   this.slideOutVisible2 = false;
  //   this.slideOutVisible = true;
  // }

  selectNone(taskType: 'Vehicle Complete' | 'Supplement' | 'Phase Change') {
    this.subletService.resetOptions(); // Reset all options
    this.closeSlideOut(); // Close the slide-out
    this.syncOptionsWithService(); // Ensure the options are reset visually
    this.selectedTask(taskType); // Load the specified task type
    this.slideOutVisible2 = false;
    this.slideOutVisible = true;
  }

  closeSlideOut() {
    this.slideOutVisible2 = false;
    this.subletService.closeSublet();
  }


  submit() {
    // Logic to handle the submission of selected options
    this.subletService.resetOptions();
    this.selectedOptions.forEach(option => {
      this.subletService.addOption(option);
    });
    this.closeSlideOut();
    this.slideOutVisible = true; 
  }

  assignedToRoleGroup(taskName: string): string {
    switch (taskName) {
      case 'In Process Photos':
      case 'Phase Change':
      case 'Supplement':
      case 'Vehicle Complete':
      case 'Supplies':
        return 'Estimator(s)';
        break;
      case 'Time Adjust':
        return 'General Manager';
        break;
      default:
        return 'Parts Coordinator(s)';
    }
  }

  toggleSubletComponent() {
    this.subletService.toggleSublet();
    this.slideOutVisible = true;
    this.showSubletButton = true;
    this.syncOptionsWithService();
  }


  getSubletPhotos(sublet: Sublet): SubletPhoto[] {
    return sublet.photos || [];
  }

  async toggleFlash() {
    try {
      // Get video stream
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: this.currentCamera
        }
      });
  
      // Get video track
      const track = stream.getVideoTracks()[0];
      const capabilities = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
      
      // Check if torch is supported
      if (capabilities.torch) {
        const constraints = track.getConstraints();
        await track.applyConstraints({
          ...constraints,
          advanced: [{
            // Type assertion for the torch property
            torch: !this.flashEnabled
          } as MediaTrackConstraintSet]
        });
        this.flashEnabled = !this.flashEnabled;
      } else {
        console.warn('Torch/flash not supported on this device');
       // this.toastService.show('Flash is not available on this device', 'warning');
      }
  
      // Clean up
      stream.getTracks().forEach(track => track.stop());
    } catch (error) {
      console.error('Error accessing camera:', error);
      this.flashEnabled = false;
      //this.toastService.show('Unable to access camera flash', 'error');
    }
  }

  switchCamera() {
    this.currentCamera = this.currentCamera === 'environment' ? 'user' : 'environment';
  }

  async onPhotoSelected(event: Event, sublet: Sublet) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.isCapturing = true;
      const file = input.files[0];
      
      try {
        const compressedFile = await this.compressImage(file);
        const url = URL.createObjectURL(compressedFile);
        
        if (!sublet.photos) {
          sublet.photos = [];
        }
        
        if (sublet.photos.length < 3) {
          const photo: SubletPhoto = { 
            url, 
            file: compressedFile,
            timestamp: Date.now(),
            rotation: 0,
            isProcessing: false
          };
          sublet.photos.push(photo);
        }

        input.value = '';
      } catch (error) {
        console.error('Error processing photo:', error);
        // Show error toast
      } finally {
        this.isCapturing = false;
      }
    }
  }

  rotatePhoto(sublet: Sublet, index: number, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    if (sublet.photos?.[index]) {
      const photo = sublet.photos[index];
      photo.isProcessing = true;
      photo.rotation = ((photo.rotation || 0) + 90) % 360;
      
      // Update the style of the image element
      const img = document.querySelector(`[data-photo-index="${index}"]`) as HTMLImageElement;
      if (img) {
        img.style.transform = `rotate(${photo.rotation}deg)`;
      }
      
      // Update the actual file in background
      this.updatePhotoFile(sublet, index).finally(() => {
        photo.isProcessing = false;
      });
    }
  }

  private async updatePhotoFile(sublet: Sublet, index: number) {
    const photo = sublet.photos[index];
    if (!photo) return;

    photo.isProcessing = true;
    try {
      const rotatedFile = await this.rotateImage(photo.file, photo.rotation || 0);
      photo.file = rotatedFile;
    } catch (error) {
      console.error('Error rotating image:', error);
    } finally {
      photo.isProcessing = false;
    }
  }

  private async rotateImage(file: File, degrees: number): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          // Set proper canvas dimensions for rotation
          if (degrees === 90 || degrees === 270) {
            canvas.width = img.height;
            canvas.height = img.width;
          } else {
            canvas.width = img.width;
            canvas.height = img.height;
          }

          // Rotate and draw the image
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((degrees * Math.PI) / 180);
          ctx.drawImage(img, -img.width / 2, -img.height / 2);

          canvas.toBlob((blob) => {
            if (blob) {
              resolve(new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              }));
            } else {
              reject(new Error('Failed to create blob'));
            }
          }, 'image/jpeg', 0.8);
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  }

  openPhotoPreview(photo: SubletPhoto) {
    this.previewPhoto = photo;
  }

  closePhotoPreview() {
    this.previewPhoto = null;
  }

  // rotatePreviewPhoto(degrees: number, event: Event) {
  //   event.stopPropagation();
  //   event.preventDefault();
  //   if (this.previewPhoto) {
  //     this.previewPhoto.rotation = ((this.previewPhoto.rotation || 0) + degrees) % 360;
  //   }
  // }

  rotatePreviewPhoto(degrees: number, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    
    if (this.previewPhoto) {
      // Update preview rotation
      this.previewPhoto.rotation = ((this.previewPhoto.rotation || 0) + degrees) % 360;
      
      // Find and update the original photo in the sublets
      for (const sublet of this.selectedSubletOptions) {
        const photoIndex = sublet.photos?.findIndex(
          (          photo: { url: string | undefined; }) => photo.url === this.previewPhoto?.url
        );
        
        if (photoIndex !== undefined && photoIndex !== -1 && sublet.photos) {
          // Update original photo rotation
          sublet.photos[photoIndex].rotation = this.previewPhoto.rotation;
          
          // Process the rotated file
          this.updatePhotoFile(sublet, photoIndex);
          break;
        }
      }
    }
  }
  

  private async compressImage(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Max dimensions
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          // Convert to blob
          canvas.toBlob((blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Failed to compress image'));
            }
          }, 'image/jpeg', 0.8); // 0.8 quality
        };
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
        img.src = event.target?.result as string;
      };
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(file);
    });
  }

  removePhoto(sublet: Sublet, index: number, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (sublet.photos) {
      // Revoke object URL to prevent memory leaks
      URL.revokeObjectURL(sublet.photos[index].url);
      sublet.photos.splice(index, 1);
    }
  }

  emitTaskUpdate(task: Task, action: 'create' | 'update' | 'delete' = 'update') {
    const payload: TaskUpdatePayload = {
      tenantId: task.tenant as string,
      requestId: (task.request?._id || task.request) as string,
      action: action,
      timestamp: Date.now()
    };

    this.ws.emitTaskUpdate(payload).pipe(
      catchError(error => {
        console.error('Failed to emit task update:', error);
        return EMPTY;
      })
    ).subscribe();
  }

  private notifyDashboardPeers(requestId: string) {
    const tenantRaw = this.user?.tenant;
    const tenantId =
      typeof tenantRaw === 'object' && tenantRaw !== null && '_id' in tenantRaw
        ? String((tenantRaw as { _id: string })._id)
        : String(tenantRaw ?? '');
    if (!tenantId || !requestId) {
      return;
    }
    this.ws.emitTaskUpdate({
      tenantId,
      requestId,
      action: 'create',
      timestamp: Date.now()
    }).pipe(
      catchError(() => EMPTY)
    ).subscribe();
  }

  async onGeneralPhotoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.isCapturing = true;
      const file = input.files[0];
      try {
        const compressedFile = await this.compressImage(file);
        const url = URL.createObjectURL(compressedFile);
        if (this.generalPhotos.length < 3) {
          const photo: SubletPhoto = {
            url,
            file: compressedFile,
            timestamp: Date.now(),
            rotation: 0,
            isProcessing: false
          };
          this.generalPhotos.push(photo);
        }
        input.value = '';
      } catch (error) {
        console.error('Error processing general photo:', error);
      } finally {
        this.isCapturing = false;
      }
    }
  }

  removeGeneralPhoto(index: number, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.generalPhotos[index]) {
      URL.revokeObjectURL(this.generalPhotos[index].url);
      this.generalPhotos.splice(index, 1);
    }
  }

  showGeneralMediaUpload(requestId: any): boolean {
    if (!requestId || requestId === '') {
      return false;
    }
    const matchedRequest = this.requests.find(req => req._id === requestId);
    if (!matchedRequest) {
      return false;
    }
    const requestName = matchedRequest.name;
    return requestName === "In Process Photos" || requestName === "Phase Change";
  }

}
